/**
 * The single source of truth for everything language-related.
 *
 * Before this module existed the answer to "what languages does the
 * site support?" was written down in six places (``src/i18n/routing.ts``,
 * ``src/lib/site-locale.ts``, ``src/lib/site-data.ts``,
 * ``src/app/sitemap.ts``, ``src/lib/seo/page-metadata.ts`` and a regex
 * in ``next.config.ts``), and the answer to "what language does this
 * visitor want?" was decided by three competing mechanisms
 * (``next-intl``'s ``Accept-Language`` negotiation, its ``NEXT_LOCALE``
 * cookie sync, and a ``localStorage`` key written by the React
 * preferences provider). This file replaces all of that with one list
 * and one preference.
 *
 * ## Why this file has zero imports
 *
 * It is imported from four very different runtimes:
 *
 *   1. ``src/proxy.ts`` — the Next.js Proxy (formerly Middleware), which
 *      runs before any page renders.
 *   2. React Server Components (metadata, sitemap).
 *   3. Browser code (the preferences provider, the language switcher).
 *   4. ``node --test`` in CI, which has no bundler and therefore cannot
 *      resolve the ``@/`` path alias.
 *
 * Keeping it dependency-free means every one of those can import it
 * without dragging in Prisma, React, or the message bundles.
 *
 * ## The URL contract
 *
 * An explicit URL *always* decides the language of the response. The
 * server never looks at ``Accept-Language``.
 *
 *     /      -> English (the canonical English root)
 *     /en    -> English (kept for backwards compatibility)
 *     /tr    -> Turkish
 *     /de    -> German
 *     /uk    -> Ukrainian
 *
 * The only place a saved preference changes anything is a request for
 * ``/`` — see ``resolveRootRequest`` below.
 */

/**
 * Every language the site ships. Order matters only for documentation;
 * lookups are by value.
 *
 * Adding a language here is *most* of the work: the routing config, the
 * page-metadata alternates, the sitemap and the proxy all derive from
 * this array. You still need to add ``src/messages/<code>.json`` and the
 * matching per-language markdown under ``src/content``.
 */
export const SITE_LANGUAGES = ["en", "tr", "de", "uk"] as const;

/**
 * The union type of supported language codes. Historically this type
 * lived in ``src/lib/site-data.ts``; that module now re-exports this one
 * so the type and the runtime array can never drift apart.
 */
export type SiteLanguage = (typeof SITE_LANGUAGES)[number];

/**
 * The language served when nothing else is known. This is also the
 * language of the bare root ``/``.
 */
export const DEFAULT_SITE_LANGUAGE: SiteLanguage = "en";

/**
 * Name of the cookie holding the visitor's saved language.
 *
 * We deliberately keep ``next-intl``'s historical cookie name rather
 * than inventing a new one: visitors who already have a ``NEXT_LOCALE``
 * cookie from the previous implementation keep their preference across
 * the deploy instead of being treated as first-time visitors.
 *
 * The cookie is *not* HttpOnly on purpose. It is written exclusively by
 * the browser (see ``writeLocalePreference``) so that no page response
 * ever carries a ``Set-Cookie`` header, which is what keeps localized
 * pages eligible for shared Cloudflare caching. The server only ever
 * reads it, and only for requests to ``/``.
 */
export const LOCALE_COOKIE_NAME = "NEXT_LOCALE";

/**
 * One year, in seconds. Matches the "twelve months from the last
 * change" retention promise in the Privacy Policy. Every write refreshes
 * the window, so an active reader never silently loses their language.
 */
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Type guard: is this string one of the four supported languages?
 */
export function isSupportedSiteLanguage(value: string | null | undefined): value is SiteLanguage {
  return typeof value === "string" && (SITE_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Turn a raw cookie value into a language, or ``null`` when it is
 * missing, empty, or something we do not serve.
 *
 * Returning ``null`` for junk (``NEXT_LOCALE=fr``, ``NEXT_LOCALE=``,
 * ``NEXT_LOCALE=<script>``) is what makes an invalid cookie behave
 * exactly like no cookie at all: the visitor gets deterministic English
 * on ``/`` and the client-side bootstrap is free to overwrite the value
 * with something valid.
 */
export function normalizeLocalePreference(value: string | null | undefined): SiteLanguage | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return isSupportedSiteLanguage(trimmed) ? trimmed : null;
}

/**
 * Build the public URL for ``path`` in ``language``.
 *
 * The one asymmetry in the URL scheme lives here: the English *home
 * page* is ``/``, not ``/en``. Every other English URL keeps its
 * ``/en`` prefix, because this project deliberately does not migrate
 * ``/en/news`` to ``/news``.
 *
 *     localizedPath("en", "")        -> "/"
 *     localizedPath("en", "/news")   -> "/en/news"
 *     localizedPath("tr", "")        -> "/tr"
 *     localizedPath("tr", "/news")   -> "/tr/news"
 *
 * ``path`` is a language-agnostic path such as ``""``, ``"/"`` or
 * ``"/clusters/abc"``.
 */
export function localizedPath(language: SiteLanguage, path: string): string {
  const clean = path === "/" ? "" : path;
  if (language === DEFAULT_SITE_LANGUAGE && clean === "") {
    return "/";
  }
  return `/${language}${clean}`;
}

/**
 * Which language does this *pathname* declare?
 *
 * ``"/"`` answers ``"en"`` because the bare root is the English root.
 * A locale prefix answers itself. Anything else answers ``null`` — the
 * caller decides what to do with an unprefixed deep path.
 */
export function languageFromPathname(pathname: string): SiteLanguage | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return DEFAULT_SITE_LANGUAGE;
  return isSupportedSiteLanguage(segments[0]) ? segments[0] : null;
}

/**
 * What the server should do with a request for ``/``.
 *
 * This is the whole of the root-preference behaviour, expressed as a
 * pure function so it can be unit-tested without spinning up Next.js.
 *
 *   - A saved *non-English* preference means the visitor has been here
 *     before and told us (explicitly or through first-visit detection)
 *     which language they read. They get a temporary redirect to that
 *     language's root, issued before a single byte of HTML is rendered,
 *     so there is no English flash.
 *   - Everything else — no cookie, an English cookie, a corrupt cookie —
 *     renders English. That response depends on nothing but the URL,
 *     which is what makes ``/`` cacheable and what makes a crawler's
 *     view of ``/`` stable.
 */
export type RootRequestDecision =
  | { kind: "render"; language: SiteLanguage }
  | { kind: "redirect"; language: SiteLanguage; path: string };

export function resolveRootRequest(cookieValue: string | null | undefined): RootRequestDecision {
  const preference = normalizeLocalePreference(cookieValue);
  if (preference && preference !== DEFAULT_SITE_LANGUAGE) {
    return { kind: "redirect", language: preference, path: localizedPath(preference, "") };
  }
  return { kind: "render", language: DEFAULT_SITE_LANGUAGE };
}

/**
 * Pick a language from a browser's ordered language list.
 *
 * This is the *client-side* half of the design: the server refuses to
 * look at ``Accept-Language``, so first-visit detection happens in the
 * browser using ``navigator.languages``.
 *
 * The list is ordered by the user's own preference, so we take the first
 * entry whose primary subtag we support. ``"tr-TR"`` matches ``tr``;
 * ``"en-GB"`` matches ``en`` (and notably *not* ``uk``, which is
 * Ukrainian); anything unrecognised falls through to English.
 */
export function matchBrowserLanguage(languages: readonly string[] | null | undefined): SiteLanguage {
  if (!languages) return DEFAULT_SITE_LANGUAGE;
  for (const tag of languages) {
    if (typeof tag !== "string") continue;
    const primary = tag.trim().toLowerCase().split("-")[0];
    if (isSupportedSiteLanguage(primary)) {
      return primary;
    }
  }
  return DEFAULT_SITE_LANGUAGE;
}

// ---------------------------------------------------------------------
// Browser-side cookie access
//
// These two functions are the *only* writers of the language
// preference. Nothing on the server sets it, which is deliberate:
// a ``Set-Cookie`` header on a page response makes that response
// personalised and Cloudflare will refuse to cache it. Keeping the
// write in the browser means every HTML response stays anonymous.
// ---------------------------------------------------------------------

/**
 * Read the saved language out of ``document.cookie``. Returns ``null``
 * on the server, when the cookie is absent, or when it holds a value we
 * do not serve.
 */
export function readLocalePreference(): SiteLanguage | null {
  if (typeof document === "undefined") return null;
  const parts = document.cookie ? document.cookie.split(";") : [];
  for (const part of parts) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== LOCALE_COOKIE_NAME) continue;
    try {
      return normalizeLocalePreference(decodeURIComponent(part.slice(separator + 1).trim()));
    } catch {
      // A malformed percent-escape is treated like no cookie at all.
      return null;
    }
  }
  return null;
}

/**
 * Save the visitor's language. No-ops on the server and swallows the
 * error browsers throw when cookies are blocked entirely — a reader with
 * cookies disabled simply gets detected again on their next visit to
 * ``/``, which is a mild inefficiency and not a failure.
 */
export function writeLocalePreference(language: SiteLanguage): void {
  if (typeof document === "undefined") return;
  try {
    const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; secure" : "";
    document.cookie =
      `${LOCALE_COOKIE_NAME}=${language}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
  } catch {
    // Cookies blocked (private mode, storage partitioning) — best effort.
  }
}

/**
 * Source of the inline, render-blocking script that runs on ``/``
 * before the browser paints anything.
 *
 * ### Why a hand-written string instead of a React effect
 *
 * A ``useEffect`` runs after hydration, which means the visitor would
 * see a fully painted English home page before being sent to ``/tr``.
 * This script is the first child of ``<body>``, so it executes while the
 * rest of the document is still being parsed: calling
 * ``location.replace`` there aborts the parse and nothing is painted.
 *
 * ### Why it duplicates a little logic
 *
 * It cannot import ``matchBrowserLanguage`` — it is a string embedded in
 * the HTML, not a module. The *data* it needs (the language list, the
 * cookie name, its lifetime) is interpolated from the constants above,
 * so the only thing restated here is a five-line loop. That is the
 * smallest duplication that still buys a flash-free first visit, and
 * ``tests/locale-bootstrap.test.mts`` executes this exact string against
 * a fake DOM so the two implementations cannot silently diverge.
 *
 * ### What it does, in order
 *
 *   1. Bails out immediately unless the document is ``/``. The script
 *      ships in the root layout, so it is present on every page.
 *   2. If a supported non-English preference is already saved, it
 *      navigates to that language root. On a healthy request this never
 *      fires, because the proxy already redirected. It exists as a
 *      safety net for the one case the origin cannot control: a shared
 *      cache (a misconfigured Cloudflare rule, a corporate proxy)
 *      handing a returning reader the cached English ``/``.
 *   3. With no usable preference it reads ``navigator.languages``,
 *      saves the match, and navigates when the match is not English.
 *      English and unsupported languages save ``en`` and stay put, so
 *      the detection runs exactly once per browser.
 */
export const LOCALE_BOOTSTRAP_SCRIPT = `(function(){try{
if(window.location.pathname!=="/")return;
var S=${JSON.stringify(SITE_LANGUAGES)},D=${JSON.stringify(DEFAULT_SITE_LANGUAGE)},N=${JSON.stringify(LOCALE_COOKIE_NAME)};
function go(l){window.location.replace("/"+l+window.location.search+window.location.hash);}
var saved=null,c=document.cookie?document.cookie.split(";"):[];
for(var i=0;i<c.length;i++){var e=c[i].indexOf("=");if(e<0)continue;
if(c[i].slice(0,e).trim()!==N)continue;
var v="";try{v=decodeURIComponent(c[i].slice(e+1).trim()).toLowerCase();}catch(_){v="";}
if(S.indexOf(v)>-1)saved=v;break;}
if(saved){if(saved!==D)go(saved);return;}
var langs=(navigator.languages&&navigator.languages.length)?navigator.languages:[navigator.language||D];
var picked=D;
for(var j=0;j<langs.length;j++){var t=String(langs[j]||"").trim().toLowerCase().split("-")[0];
if(S.indexOf(t)>-1){picked=t;break;}}
document.cookie=N+"="+picked+"; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; samesite=lax"+(window.location.protocol==="https:"?"; secure":"");
if(picked!==D)go(picked);
}catch(e){}})();`;
